/**
 * MagicWandManager - Manages the magic wand color removal tool with live preview
 */

import $ from 'jquery';
import { fabric } from 'fabric';
import { removeColorFromImageData, hexToRgb, rgbToHex } from './colorRemoval';
import { triggerStateChangedRequest } from '~core';
import { trackEvent } from '~src/tracking';
import { onErrorShowMessage } from '~src/errors';

class MagicWandManager {
  constructor() {
    this.isActive = false;
    this.isPickingColor = false;
    this.isPickingFromPreview = false; // New flag for preview color picking
    this.targetColor = '#ffffff'; // Default to white
    this.tolerance = 50;
    this.smoothingType = 'feather'; // Default to feather
    this.smoothRadius = 5;
    this.connectedOnly = true; // Default to connected only
    this.selectedObject = null;
    this.previewCanvas = null;
    this.previewCtx = null;
    this.originalImageData = null;
    this.previewUpdateTimeout = null;
    this.clickPoint = null; // Store click point for connected mode
  }

  /**
   * Initialize the Magic Wand tool
   */
  init() {
    this.setupUI();
    this.attachEventListeners();
  }

  /**
   * Setup UI elements for the Magic Wand tool
   */
  setupUI() {
    // Create color picker modal if it doesn't exist
    if (!$('#magic-wand-modal').length) {
      const modalHtml = `
        <div id="magic-wand-modal" class="modal fade" tabindex="-1" role="dialog">
          <div class="modal-dialog modal-xl" role="document" style="max-width: 90%; width: 1200px;">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">Bacchetta Magica - Rimuovi Colore</h5>
                <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div class="modal-body">
                <div class="row">
                  <div class="col-md-5">
                    <div class="form-group">
                      <label for="magic-wand-color">Colore da rimuovere:</label>
                      <div class="input-group">
                        <input type="color" id="magic-wand-color-picker" value="#ffffff" class="form-control" style="width: 60px; height: 38px;">
                        <input type="text" id="magic-wand-hex-input" value="#ffffff" class="form-control" placeholder="#ffffff">
                        <div class="input-group-append">
                          <button class="btn btn-outline-secondary" type="button" id="magic-wand-pick-color">
                            🎨 Preleva
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div class="form-group">
                      <label for="magic-wand-tolerance">Tolleranza: <span id="magic-wand-tolerance-value">50</span></label>
                      <input type="range" class="slider" id="magic-wand-tolerance" min="0" max="100" value="50">
                    </div>
                    
                    <div class="form-group">
                      <label for="magic-wand-smoothing">Tipo di sfumatura:</label>
                      <select class="form-control" id="magic-wand-smoothing">
                        <option value="none">Nessuna</option>
                        <option value="basic">Base</option>
                        <option value="gaussian">Gaussiana</option>
                        <option value="feather" selected>Piuma</option>
                        <option value="aggressive">Aggressiva</option>
                      </select>
                    </div>
                    
                    <div class="form-group">
                      <label for="magic-wand-smooth-radius">Raggio sfumatura: <span id="magic-wand-radius-value">5</span></label>
                      <input type="range" class="slider" id="magic-wand-smooth-radius" min="1" max="10" value="5">
                    </div>
                    
                    <div class="form-group">
                      <div class="custom-control custom-switch">
                        <input type="checkbox" class="custom-control-input" id="magic-wand-connected" checked>
                        <label class="custom-control-label" for="magic-wand-connected">
                          <strong>Solo colori connessi</strong><br>
                          <small class="text-muted">Rimuove solo i pixel collegati dai bordi (evita buchi)</small>
                        </label>
                      </div>
                    </div>
                    
                    <div class="alert alert-info">
                      <small>
                        <strong>Suggerimenti:</strong><br>
                        • Usa il pulsante "Preleva" per selezionare un colore dall'immagine<br>
                        • Aumenta la tolleranza per rimuovere più sfumature del colore<br>
                        • Attiva "Solo colori connessi" per evitare di creare buchi<br>
                        • L'anteprima mostra in tempo reale l'area che verrà resa trasparente
                      </small>
                    </div>
                  </div>
                  
                  <div class="col-md-7">
                    <div class="form-group">
                      <label>Anteprima:</label>
                      <div id="magic-wand-preview-container" style="position: relative; background-image: linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%); background-size: 20px 20px; background-position: 0 0, 0 10px, 10px -10px, -10px 0px; border: 2px solid #ddd; border-radius: 4px; overflow: hidden; min-height: 400px; display: flex; align-items: center; justify-content: center;">
                        <canvas id="magic-wand-preview" style="display: block; max-width: 100%; max-height: 600px; height: auto; cursor: crosshair;"></canvas>
                        <div id="magic-wand-click-indicator" style="display: none; position: absolute; width: 10px; height: 10px; border: 2px solid #ff0000; border-radius: 50%; background: rgba(255,0,0,0.3); pointer-events: none; transform: translate(-50%, -50%);"></div>
                        <div id="magic-wand-picking-indicator" style="display: none; position: absolute; top: 10px; right: 10px; background: rgba(0,0,0,0.7); color: white; padding: 5px 10px; border-radius: 3px; font-size: 12px;">🎨 Clicca per prelevare colore</div>
                      </div>
                      <div class="d-flex justify-content-between align-items-center mt-2">
                        <small class="text-muted">Le aree trasparenti sono mostrate con sfondo sfumato</small>
                        <div class="btn-group btn-group-sm" role="group">
                          <button type="button" class="btn btn-outline-secondary" id="magic-wand-bg-gradient" title="Sfondo sfumato">
                            🌈
                          </button>
                          <button type="button" class="btn btn-outline-secondary active" id="magic-wand-bg-checker" title="Sfondo a scacchi">
                            ▦
                          </button>
                        </div>
                      </div>
                      <small class="text-muted" id="magic-wand-connected-hint">⚡ Clicca sull'anteprima per selezionare il punto di partenza per la rimozione connessa</small>
                    </div>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-dismiss="modal">Annulla</button>
                <button type="button" class="btn btn-primary" id="magic-wand-apply">Applica</button>
              </div>
            </div>
          </div>
        </div>
      `;
      $('body').append(modalHtml);
    }

    // No need for overlay anymore - we pick from preview
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Main tool button
    $('body').on('click', '#editor-menu-magic-wand', () =>
      this.startMagicWand()
    );

    // Modal controls with live preview
    $('body').on('input', '#magic-wand-color-picker', (e) => {
      $('#magic-wand-hex-input').val(e.target.value);
      this.targetColor = e.target.value;
      this.updatePreview();
    });

    // Connected mode toggle
    $('body').on('change', '#magic-wand-connected', (e) => {
      this.connectedOnly = e.target.checked;
      this.updatePreview();
    });

    $('body').on('input', '#magic-wand-hex-input', (e) => {
      let value = e.target.value;
      if (!value.startsWith('#')) {
        value = '#' + value;
      }
      if (/^#[0-9A-F]{6}$/i.test(value)) {
        $('#magic-wand-color-picker').val(value);
        this.targetColor = value;
        this.updatePreview();
      }
    });

    $('body').on('input', '#magic-wand-tolerance', (e) => {
      $('#magic-wand-tolerance-value').text(e.target.value);
      this.tolerance = parseInt(e.target.value);
      this.updatePreview();
    });

    $('body').on('change', '#magic-wand-smoothing', (e) => {
      this.smoothingType = e.target.value;
      this.updatePreview();
    });

    $('body').on('input', '#magic-wand-smooth-radius', (e) => {
      $('#magic-wand-radius-value').text(e.target.value);
      this.smoothRadius = parseInt(e.target.value);
      this.updatePreview();
    });

    // Color picker from preview image
    $('body').on('click', '#magic-wand-pick-color', () =>
      this.startPreviewColorPicking()
    );

    // Apply button
    $('body').on('click', '#magic-wand-apply', () => this.applyColorRemoval());

    // Click on preview for color picking or connected mode starting point
    $('body').on('click', '#magic-wand-preview', (e) => {
      const canvas = e.target;
      const rect = canvas.getBoundingClientRect();
      const x = Math.floor(
        (e.clientX - rect.left) * (canvas.width / rect.width)
      );
      const y = Math.floor(
        (e.clientY - rect.top) * (canvas.height / rect.height)
      );

      // If in color picking mode, pick the color
      if (this.isPickingFromPreview) {
        this.pickColorFromPreview(x, y);
        return;
      }

      // If in connected mode, set starting point
      if (this.connectedOnly) {
        this.clickPoint = { x, y };

        // Show click indicator
        const indicator = $('#magic-wand-click-indicator');
        const containerRect = $(
          '#magic-wand-preview-container'
        )[0].getBoundingClientRect();
        indicator.css({
          left: e.clientX - containerRect.left + 'px',
          top: e.clientY - containerRect.top + 'px',
          display: 'block',
        });

        this.updatePreview();
      }
    });

    // Background style toggle
    $('body').on('click', '#magic-wand-bg-gradient', () => {
      $('#magic-wand-bg-gradient').addClass('active');
      $('#magic-wand-bg-checker').removeClass('active');
      const container = $('#magic-wand-preview').parent();
      container.css(
        'background',
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      );
    });

    $('body').on('click', '#magic-wand-bg-checker', () => {
      $('#magic-wand-bg-checker').addClass('active');
      $('#magic-wand-bg-gradient').removeClass('active');
      const container = $('#magic-wand-preview').parent();
      container.css({
        'background-image':
          'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
        'background-size': '20px 20px',
        'background-position': '0 0, 0 10px, 10px -10px, -10px 0px',
      });
    });

    // Clean up on modal close
    $('#magic-wand-modal').on('hidden.bs.modal', () => {
      this.reset();
    });

    // Cancel color picking on ESC or right-click
    $(document).on('keydown.magicwand', (e) => {
      if (e.key === 'Escape' && this.isPickingFromPreview) {
        this.cancelColorPicking();
      }
    });

    $('body').on('contextmenu', '#magic-wand-preview', (e) => {
      if (this.isPickingFromPreview) {
        e.preventDefault();
        this.cancelColorPicking();
        return false;
      }
    });
  }

  /**
   * Start the Magic Wand tool
   */
  async startMagicWand() {
    try {
      const canvas = window.canvas;
      const activeObject = canvas.getActiveObject();

      if (!activeObject || activeObject.type !== 'image') {
        onErrorShowMessage(
          "Seleziona un'immagine per usare la Bacchetta Magica"
        );
        return;
      }

      this.selectedObject = activeObject;

      // Setup preview canvas
      this.setupPreviewCanvas();

      // Show the modal
      $('#magic-wand-modal').modal('show');

      // Initial preview
      this.updatePreview();

      // Track event
      trackEvent('magic_wand_started');
    } catch (error) {
      console.error('Error starting Magic Wand:', error);
      onErrorShowMessage("Errore nell'avvio della Bacchetta Magica");
    }
  }

  /**
   * Setup preview canvas with original image
   */
  setupPreviewCanvas() {
    const previewCanvas = document.getElementById('magic-wand-preview');
    if (!previewCanvas) return;

    this.previewCanvas = previewCanvas;
    this.previewCtx = previewCanvas.getContext('2d');

    // Get the image element or create from Fabric object
    const fabricImage = this.selectedObject;

    // Calculate dimensions maintaining aspect ratio - bigger preview
    const maxWidth = 600;
    const maxHeight = 600;
    const scale = Math.min(
      maxWidth / (fabricImage.width * fabricImage.scaleX),
      maxHeight / (fabricImage.height * fabricImage.scaleY),
      1
    );

    const width = fabricImage.width * fabricImage.scaleX * scale;
    const height = fabricImage.height * fabricImage.scaleY * scale;

    // Set canvas dimensions
    previewCanvas.width = width;
    previewCanvas.height = height;

    // Create temporary canvas to get original image data
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    tempCanvas.width = width;
    tempCanvas.height = height;

    // Draw the fabric image to temp canvas
    fabricImage.cloneAsImage((clonedImage) => {
      tempCtx.drawImage(clonedImage.getElement(), 0, 0, width, height);

      // Store original image data
      this.originalImageData = tempCtx.getImageData(0, 0, width, height);

      // Initial preview
      this.updatePreview();
    });
  }

  /**
   * Update preview with current settings
   */
  updatePreview() {
    // Debounce preview updates for performance
    if (this.previewUpdateTimeout) {
      clearTimeout(this.previewUpdateTimeout);
    }

    this.previewUpdateTimeout = setTimeout(() => {
      this.performPreviewUpdate();
    }, 100);
  }

  /**
   * Perform the actual preview update
   */
  performPreviewUpdate() {
    if (!this.previewCanvas || !this.originalImageData) return;

    try {
      // Clone original image data
      const imageData = new ImageData(
        new Uint8ClampedArray(this.originalImageData.data),
        this.originalImageData.width,
        this.originalImageData.height
      );

      // Convert hex to RGB
      const targetColorRgb = hexToRgb(this.targetColor);
      if (!targetColorRgb) return;

      // Apply color removal
      const processedData = removeColorFromImageData(
        imageData,
        targetColorRgb,
        this.tolerance,
        this.smoothingType,
        this.smoothRadius,
        this.connectedOnly,
        this.clickPoint
      );

      // Display on preview canvas
      this.previewCtx.putImageData(processedData, 0, 0);
    } catch (error) {
      console.error('Error updating preview:', error);
    }
  }

  /**
   * Start color picking from preview
   */
  startPreviewColorPicking() {
    this.isPickingFromPreview = true;

    // Change cursor and show indicator
    $('#magic-wand-preview').css('cursor', 'crosshair');
    $('#magic-wand-picking-indicator').show();

    // Highlight the pick button
    $('#magic-wand-pick-color')
      .addClass('btn-primary')
      .removeClass('btn-outline-secondary');
  }

  /**
   * Cancel color picking mode
   */
  cancelColorPicking() {
    this.isPickingFromPreview = false;
    $('#magic-wand-preview').css(
      'cursor',
      this.connectedOnly ? 'crosshair' : 'default'
    );
    $('#magic-wand-picking-indicator').hide();
    $('#magic-wand-pick-color')
      .removeClass('btn-primary')
      .addClass('btn-outline-secondary');
  }

  /**
   * Pick color from preview canvas at coordinates
   */
  pickColorFromPreview(x, y) {
    if (!this.isPickingFromPreview || !this.originalImageData) return;

    try {
      // Get color from original image data (not processed)
      const idx = (y * this.originalImageData.width + x) * 4;
      const data = this.originalImageData.data;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];

      const hex = rgbToHex(r, g, b);

      // Update color inputs
      $('#magic-wand-hex-input').val(hex);
      $('#magic-wand-color-picker').val(hex);
      this.targetColor = hex;

      // Exit picking mode
      this.isPickingFromPreview = false;
      $('#magic-wand-preview').css(
        'cursor',
        this.connectedOnly ? 'crosshair' : 'default'
      );
      $('#magic-wand-picking-indicator').hide();
      $('#magic-wand-pick-color')
        .removeClass('btn-primary')
        .addClass('btn-outline-secondary');

      // Update preview with new color
      this.updatePreview();
    } catch (error) {
      console.error('Error picking color from preview:', error);
      onErrorShowMessage('Errore nel prelevare il colore');
    }
  }

  /**
   * Apply color removal to the selected image
   */
  async applyColorRemoval() {
    try {
      if (!this.selectedObject) {
        onErrorShowMessage('Nessuna immagine selezionata');
        return;
      }

      const canvas = window.canvas;

      // Convert hex to RGB
      const targetColorRgb = hexToRgb(this.targetColor);
      if (!targetColorRgb) {
        onErrorShowMessage('Colore non valido');
        return;
      }

      // Create a new canvas for processing
      const processCanvas = document.createElement('canvas');
      const processCtx = processCanvas.getContext('2d');

      // Set dimensions to match original image
      const width = this.selectedObject.width * this.selectedObject.scaleX;
      const height = this.selectedObject.height * this.selectedObject.scaleY;
      processCanvas.width = width;
      processCanvas.height = height;

      // Draw the original image
      this.selectedObject.cloneAsImage((clonedImage) => {
        processCtx.drawImage(clonedImage.getElement(), 0, 0, width, height);

        // Get and process image data
        let imageData = processCtx.getImageData(0, 0, width, height);
        imageData = removeColorFromImageData(
          imageData,
          targetColorRgb,
          this.tolerance,
          this.smoothingType,
          this.smoothRadius,
          this.connectedOnly,
          null // For final application, we don't use click point - flood from edges
        );

        // Put processed data back
        processCtx.putImageData(imageData, 0, 0);

        // Create new Fabric image from processed canvas
        fabric.Image.fromURL(processCanvas.toDataURL(), (newImage) => {
          // Copy properties from original image
          newImage.set({
            left: this.selectedObject.left,
            top: this.selectedObject.top,
            angle: this.selectedObject.angle,
            opacity: this.selectedObject.opacity,
            scaleX: this.selectedObject.scaleX,
            scaleY: this.selectedObject.scaleY,
            flipX: this.selectedObject.flipX,
            flipY: this.selectedObject.flipY,
            selectable: this.selectedObject.selectable,
            evented: this.selectedObject.evented,
            objReferenceId: this.selectedObject.objReferenceId,
          });

          // Replace the original image with processed one
          const index = canvas.getObjects().indexOf(this.selectedObject);
          canvas.remove(this.selectedObject);
          canvas.insertAt(newImage, index);
          canvas.setActiveObject(newImage);
          canvas.renderAll();

          // Update state
          triggerStateChangedRequest();

          // Hide modal
          $('#magic-wand-modal').modal('hide');

          // Track event
          trackEvent('magic_wand_applied', {
            color: this.targetColor,
            tolerance: this.tolerance,
            smoothing: this.smoothingType,
          });

          // Reset
          this.reset();
        });
      });
    } catch (error) {
      console.error('Error applying color removal:', error);
      onErrorShowMessage("Errore nell'applicare la rimozione del colore");
    }
  }

  /**
   * Reset the tool state
   */
  reset() {
    this.isActive = false;
    this.isPickingColor = false;
    this.isPickingFromPreview = false;
    this.selectedObject = null;
    this.originalImageData = null;
    this.previewCanvas = null;
    this.previewCtx = null;
    this.clickPoint = null;
    // Reset to defaults
    this.connectedOnly = true;
    this.smoothingType = 'feather';
    $('#magic-wand-click-indicator').hide();
    $('#magic-wand-picking-indicator').hide();
    $('#magic-wand-connected').prop('checked', true);
    $('#magic-wand-connected-hint').removeClass('d-none');
    $('#magic-wand-smoothing').val('feather');
    $('#magic-wand-pick-color')
      .removeClass('btn-primary')
      .addClass('btn-outline-secondary');
    $('#magic-wand-preview').css('cursor', 'crosshair'); // Default cursor for connected mode

    // Clean up event listeners
    $(document).off('keydown.magicwand');

    if (this.previewUpdateTimeout) {
      clearTimeout(this.previewUpdateTimeout);
      this.previewUpdateTimeout = null;
    }
  }

  /**
   * API method to remove color from an image programmatically
   * @param {fabric.Image} image - Fabric.js image object
   * @param {Object} options - Processing options
   * @returns {Promise<fabric.Image>} - Processed image
   */
  async removeColorFromImage(image, options = {}) {
    return new Promise((resolve) => {
      const targetColorRgb = options.color
        ? hexToRgb(options.color)
        : { r: 255, g: 255, b: 255 };
      const tolerance = options.tolerance || 50;
      const smoothingType = options.smoothing || 'gaussian';
      const smoothRadius = options.radius || 5;

      // Create processing canvas
      const processCanvas = document.createElement('canvas');
      const processCtx = processCanvas.getContext('2d');

      const width = image.width * image.scaleX;
      const height = image.height * image.scaleY;
      processCanvas.width = width;
      processCanvas.height = height;

      // Process the image
      image.cloneAsImage((clonedImage) => {
        processCtx.drawImage(clonedImage.getElement(), 0, 0, width, height);

        let imageData = processCtx.getImageData(0, 0, width, height);
        imageData = removeColorFromImageData(
          imageData,
          targetColorRgb,
          tolerance,
          smoothingType,
          smoothRadius,
          false, // API method doesn't use connected mode by default
          null
        );

        processCtx.putImageData(imageData, 0, 0);

        window.fabric.Image.fromURL(processCanvas.toDataURL(), (newImage) => {
          newImage.set({
            left: image.left,
            top: image.top,
            angle: image.angle,
            opacity: image.opacity,
            scaleX: image.scaleX,
            scaleY: image.scaleY,
            flipX: image.flipX,
            flipY: image.flipY,
            selectable: image.selectable,
            evented: image.evented,
            objReferenceId: image.objReferenceId,
          });

          resolve(newImage);
        });
      });
    });
  }
}

// Create singleton instance
const magicWandManager = new MagicWandManager();

export default magicWandManager;
